import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useParams, useNavigate, NavLink, Outlet, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { listDeliveryDrivers } from '@vn-propane/shared/client';
import type { DeliveryDriverWithStations, ErrorResponse } from '@vn-propane/shared/client';
import { User, Plus, ChevronRight, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DeliveryDriverContextType {
  driver: DeliveryDriverWithStations | null;
  refreshDrivers: () => void;
}

const DeliveryDriverContext = createContext<DeliveryDriverContextType>({
  driver: null,
  refreshDrivers: () => {},
});

export function useDeliveryDriver() {
  return useContext(DeliveryDriverContext);
}

export default function DeliveryDriversLayout() {
  const { id: orgId, driverId } = useParams<{ id: string; driverId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const [drivers, setDrivers] = useState<DeliveryDriverWithStations[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDrivers = useCallback(async () => {
    if (!orgId) return;

    setLoading(true);
    try {
      const result = await listDeliveryDrivers({ path: { org_id: orgId } });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        if (result.response?.status === 401) {
          navigate('/login');
          return;
        }
        setError(errorResponse.error?.message || t('deliveryDrivers.failedToLoad'));
        return;
      }

      if (result.data) {
        setDrivers(result.data.data);
      }
      setError(null);
    } catch (err) {
      setError(t('deliveryDrivers.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orgId, navigate]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const selectedDriver = drivers.find(d => d.id === driverId) || null;

  const formatGender = (gender: string | null | undefined) => {
    if (!gender) return t('deliveryDrivers.genderNotSpecified');
    const genderKey = gender.toLowerCase();
    if (genderKey === 'male') return t('deliveryDrivers.male');
    if (genderKey === 'female') return t('deliveryDrivers.female');
    if (genderKey === 'other') return t('deliveryDrivers.other');
    return gender.charAt(0).toUpperCase() + gender.slice(1);
  };

  if (loading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  const tabClass = cn(
    'relative h-10 rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 font-medium text-muted-foreground transition-none',
    'hover:text-foreground'
  );
  const activeTabClass = 'border-primary text-foreground';

  return (
    <DeliveryDriverContext.Provider value={{ driver: selectedDriver, refreshDrivers: fetchDrivers }}>
      <div className="flex flex-col md:flex-row md:min-h-[calc(100vh-4rem)] -m-4 md:-m-8">
        {/* Left column - Delivery Drivers List */}
        <div className={cn(
          'md:w-56 flex flex-col border-b md:border-b-0 md:border-r',
          selectedDriver && 'hidden md:flex'
        )}>
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
              <h2 className="font-semibold">{t('deliveryDrivers.drivers')}</h2>
              <Button size="sm" variant="ghost" asChild title={t('deliveryDrivers.addDriver')}>
                <Link to={`/organizations/${orgId}/drivers/new`}>
                  <Plus className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {drivers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <p>{t('deliveryDrivers.noDriversYet')}</p>
                <Button variant="link" size="sm" asChild>
                  <Link to={`/organizations/${orgId}/drivers/new`}>
                    {t('deliveryDrivers.addYourFirstDriver')}
                  </Link>
                </Button>
              </div>
            ) : (
              <ul className="space-y-1">
                {drivers.map((driver) => {
                  const effectiveName = driver.display_name || driver.full_name;
                  return (
                    <li key={driver.id}>
                      <NavLink
                        to={`/organizations/${orgId}/drivers/${driver.id}`}
                        className={({ isActive }) => cn(
                          'w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between group',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted'
                        )}
                      >
                        {({ isActive }) => (
                          <>
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate">{effectiveName}</div>
                              <div className={cn(
                                'text-xs truncate flex items-center gap-1',
                                isActive
                                  ? 'text-primary-foreground/70'
                                  : 'text-muted-foreground'
                              )}>
                                <User className="w-3 h-3 shrink-0" />
                                <span>{t('deliveryDrivers.stationCount', { count: driver.gas_station_ids.length })}</span>
                              </div>
                            </div>
                            <ChevronRight className={cn(
                              'w-4 h-4 shrink-0 ml-2',
                              isActive
                                ? 'text-primary-foreground/70'
                                : 'text-muted-foreground opacity-0 group-hover:opacity-100'
                            )} />
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Content area with tabs */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {selectedDriver ? (
            <>
              <div className="p-4 md:p-6 pb-0 border-b">
                <button
                  onClick={() => navigate(`/organizations/${orgId}/drivers`)}
                  className="md:hidden inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('deliveryDrivers.drivers')}
                </button>
                <h2 className="text-xl md:text-2xl font-extrabold tracking-tight">{selectedDriver.display_name || selectedDriver.full_name}</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  {selectedDriver.display_name && selectedDriver.display_name !== selectedDriver.full_name && (
                    <span className="mr-2">({selectedDriver.full_name})</span>
                  )}
                  {formatGender(selectedDriver.gender)}
                  {selectedDriver.birthdate && ` · ${t('deliveryDrivers.born', { date: selectedDriver.birthdate })}`}
                </p>
              </div>

              <div className="border-b px-4 md:px-6 overflow-x-auto">
                <nav className="flex gap-4 h-10 min-w-max">
                  <NavLink
                    to={`/organizations/${orgId}/drivers/${driverId}`}
                    end
                    className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
                  >
                    {t('deliveryDrivers.overview')}
                  </NavLink>
                  <NavLink
                    to={`/organizations/${orgId}/drivers/${driverId}/assignments`}
                    className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
                  >
                    {t('deliveryDrivers.assignments')}
                  </NavLink>
                  <NavLink
                    to={`/organizations/${orgId}/drivers/${driverId}/settings`}
                    className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
                  >
                    {t('deliveryDrivers.settings')}
                  </NavLink>
                  <NavLink
                    to={`/organizations/${orgId}/drivers/${driverId}/invite-codes`}
                    className={({ isActive }) => cn(tabClass, isActive && activeTabClass)}
                  >
                    {t('deliveryDrivers.inviteCodes')}
                  </NavLink>
                </nav>
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-6">
                <Outlet />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              {t('deliveryDrivers.selectDriver')}
            </div>
          )}
        </div>
      </div>
    </DeliveryDriverContext.Provider>
  );
}
